import {
  FormCheckbox,
  FormCombobox,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components";
import { Button, Card } from "@/components/ui";
import { stateList } from "@/consts";
import { getDayOptions, yearOptions, yearRangeOptions } from "@/lib/utils";
import { routes } from "@/routes/utils";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { TabsDataTypes } from "../types";
import {
  ADDRESS1,
  ADDRESS2,
  CITY,
  EDUCATOR_TYPE,
  educatorOptions,
  EMAIL,
  FACULTY_DAYS,
  GRADES_SELECTED,
  INCLUDES_BOOKS,
  MAINTENANCE,
  maintenanceOptions,
  NO_FACULTY_DAYS,
  NO_OF_WORKSTATIONS,
  PHONE,
  REGION,
  SCHOOL_LOGO,
  SCHOOL_NAME,
  SCHOOL_TIER,
  SKU,
  skuOptions,
  STATE,
  SYLLABUS,
  syllabusOptions,
  accounttypeoptions,
  tierOptions,
  WEBSITE,
  YEARS_OF_COLLABORATION,
  ZIP,
  ACCOUNT_TYPE,
  STUDENT_STRENGTH,
  booksIncludeoptions
} from "./consts";
import useSchoolInfo from "./useSchoolInfo";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";


const SchoolInfo: FC<TabsDataTypes> = ({ onNextClick }) => {
  const { action, id } = useParams();
  const {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    gradeOptions,
    gradesLoading,
    regionsLoading,
    regionOptions,
    isLoading,
  } = useSchoolInfo({ onNextClick });
  const { errors } = form.formState;
  const isView = action == routes.view;
  const isEdit = action == routes.edit;

   const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  const { control } = form;
  const skuWatch = useWatch({ control, name: SKU });
  const eduTypeWatch = useWatch({ control, name: EDUCATOR_TYPE });

  return (
    <Card className="p-5 pt-0 shadow-none">
      {isLoading && <div className="py-2 text-white">Loading...</div>}
      {!isLoading && (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg pt-3 pb-[100px] px-1">
              <h2 className="text-[16px] text-primary mb-5">Basic Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px]">
                <FormInput
                  name={SCHOOL_NAME}
                  control={form.control}
                  label="School Name"
                  placeholder="Please enter"
                  error={errors[SCHOOL_NAME]?.message}
                  isRequired={requiredFields.includes(SCHOOL_NAME)}
                  maxLength={50}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }

                />
                <FormCombobox
                  name={STATE}
                  control={form.control}
                  label="State"
                  placeholder="Please enter"
                  error={errors[STATE]?.message}
                  isRequired={requiredFields.includes(STATE)}
                  options={Object.keys(stateList).map((val: string) => ({
                    label: val,
                    value: val,
                  }))}
                    disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }

                />
                <FormCombobox
                  name={CITY}
                  control={form.control}
                  label="City"
                  placeholder="Please enter"
                  error={errors[CITY]?.message}
                  isRequired={requiredFields.includes(CITY)}
                  disabled={!form.watch(STATE) || isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))}
                  options={
                    (stateList as any)[form.watch(STATE)]?.map(
                      (val: string) => ({
                        label: val,
                        value: val,
                      })
                    ) || []
                  }
                />
                <FormInput
                  name={ZIP}
                  control={form.control}
                  label="Zip"
                  placeholder="Please enter"
                  error={errors[ZIP]?.message}
                  isRequired={requiredFields.includes(ZIP)}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                />
                <FormCombobox
                  name={REGION}
                  control={form.control}
                  label="Region"
                  placeholder="Please select"
                  error={errors[REGION]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(REGION)}
                  options={regionOptions}
                  loading={regionsLoading}
                />
                <FormInput
                  name={PHONE}
                  control={form.control}
                  label="Phone No."
                  placeholder="Please enter"
                  error={errors[PHONE]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(PHONE)}
                  variant="integer"
                  maxLength={10}
                />
                <FormInput
                  name={EMAIL}
                  control={form.control}
                  label="Email ID"
                  placeholder="Please enter"
                  error={errors[EMAIL]?.message}
                  isRequired={requiredFields.includes(EMAIL)}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                />
                <FormInput
                  name={WEBSITE}
                  control={form.control}
                  label="Website"
                  placeholder="Please enter"
                  error={errors[WEBSITE]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(WEBSITE)}
                />
                <FormFileInput
                  fileInputRef={attachmentRef}
                  name={SCHOOL_LOGO}
                  control={form.control}
                  label="School Logo"
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  error={errors[SCHOOL_LOGO]?.message}
                  isRequired={requiredFields.includes(SCHOOL_LOGO)}
                  maxSize={5} // in MB
                />
                <div className="md:col-span-2 lg:col-span-3">
                  <FormTextarea
                    name={ADDRESS1}
                    control={form.control}
                    label="Address 1"
                    placeholder="Please enter"
                    error={errors[ADDRESS1]?.message}
                    isRequired={requiredFields.includes(ADDRESS1)}
                    disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                    maxLength={75}
                  />
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                  <FormTextarea
                    name={ADDRESS2}
                    control={form.control}
                    label="Address 2"
                    placeholder="Please enter"
                    error={errors[ADDRESS2]?.message}
                    isRequired={requiredFields.includes(ADDRESS2)}
                    disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                    maxLength={75}
                  />
                </div>
              </div>

              <h2 className="text-[16px] text-primary my-5">
                Academic and Operational Info
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px]">
                <FormSelect
                  name={YEARS_OF_COLLABORATION}
                  control={form.control}
                  label="Academic Collaboration"
                  placeholder="Please Select"
                  error={errors[YEARS_OF_COLLABORATION]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(YEARS_OF_COLLABORATION)}
                  options={yearRangeOptions()}
                />
                <FormCombobox
                  name={GRADES_SELECTED}
                  control={form.control}
                  label="Grades Selected"
                  placeholder="Please Select"
                  error={errors[GRADES_SELECTED]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(GRADES_SELECTED)}
                  options={gradeOptions}
                  loading={gradesLoading}
                  multiSelect
                />
                <FormSelect
                  name={EDUCATOR_TYPE}
                  control={form.control}
                  label="Educator Type"
                  placeholder="Please Select"
                  error={errors[EDUCATOR_TYPE]?.message}
                  disabled={
                    isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                  }
                  isRequired={requiredFields.includes(EDUCATOR_TYPE)}
                  options={educatorOptions}
                />
                <FormCombobox
                  name={FACULTY_DAYS}
                  control={form.control}
                  label="Faculty Days"
                  placeholder="Please Select"
                  // error={errors[FACULTY_DAYS]?.message}
                  disabled={
                    isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN")) || eduTypeWatch === "Non-Educator" ? true : false
                    }
                  isRequired={requiredFields.includes(FACULTY_DAYS)}
                  options={getDayOptions()}
                  multiSelect
                />
                <FormInput
                  name={NO_FACULTY_DAYS}
                  control={form.control}
                  label="# Faculty Days"
                  placeholder="Please enter"
                  error={errors[NO_FACULTY_DAYS]?.message}
                  isRequired={requiredFields.includes(NO_FACULTY_DAYS)}
                  disabled
                />
                <FormSelect
                  name={MAINTENANCE}
                  control={form.control}
                  label="Maintenance"
                  placeholder="Please Select"
                  error={errors[MAINTENANCE]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(MAINTENANCE)}
                  options={maintenanceOptions}
                />
                <FormSelect
                  name={SKU}
                  control={form.control}
                  label="SKU"
                  placeholder="Please Select"
                  error={errors[SKU]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(SKU)} 
                  options={skuOptions}
                />
                <FormInput
                  name={NO_OF_WORKSTATIONS}
                  control={form.control}
                  label="No. of Workstations"
                  placeholder="Please enter"
                  error={errors[NO_OF_WORKSTATIONS]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(NO_OF_WORKSTATIONS)}
                  variant="integer"
                />
                <FormSelect
                  name={INCLUDES_BOOKS}
                  control={form.control}
                  label="Includes Books?"
                  placeholder="Please Select"
                  error={errors[INCLUDES_BOOKS]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(INCLUDES_BOOKS)}
                  options={booksIncludeoptions}
                />
                <FormSelect
                  name={SCHOOL_TIER}
                  control={form.control}
                  label="School Tier"
                  placeholder="Please Select"
                  error={errors[SCHOOL_TIER]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(SCHOOL_TIER)}
                  options={tierOptions}
                />
                <FormCombobox
                  name={SYLLABUS}
                  control={form.control}
                  label="Syllabus"
                  placeholder="Please Select"
                  error={errors[SYLLABUS]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(SYLLABUS)}
                  options={syllabusOptions}
                  multiSelect
                />
                {
                  skuWatch === "Standard" &&
                  <FormSelect
                    name={ACCOUNT_TYPE}
                    control={form.control}
                    label="AMC Package"
                    placeholder="Please Select"
                    error={errors[ACCOUNT_TYPE]?.message}
                    disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                    isRequired={requiredFields.includes(ACCOUNT_TYPE)}
                    options={accounttypeoptions}
                  />
                }
                <FormInput
                  name={STUDENT_STRENGTH}
                  control={form.control}
                  label="Student Strength"
                  placeholder="Please Select"
                  error={errors[STUDENT_STRENGTH]?.message}
                  disabled={
                      isView || !(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN"))
                    }
                  isRequired={requiredFields.includes(STUDENT_STRENGTH)}         
                />
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-0 w-full bg-white pb-[25px] z-[3]">
              <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button>
              {(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN")) && (
              <Button type="submit">
                {isEdit ? "Update" : "Save & Next"}
              </Button>
            )}
            </div>
          </form>
        </FormProvider>
      )}
    </Card>
  );
};

export default SchoolInfo;
